import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { X, Plus, Minus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { RootState } from '../../app/store'
import { useGetCashierProductDetailsQuery } from '../../features/menu/menuAPI'
import { setLoading, setError, updateCartFromAPI } from '../../features/cart/cartSlice'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../app/store'
import { useAddToCartWithItemsMutation, useLazyGetCartDataQuery } from '../../features/cart/cartAPI'
import { loadCartItems } from '../../features/cart/cartItemsSlice'
import { selectSelectedTypeId } from '../../features/csFlow/csFlowSlice'
import { refreshCsCartItems } from '../../features/csFlow/csCartSlice'
import apiService from '../../services/api'
import { buildEndpoint } from '../../utils/apiUtils'
import CurrencyDisplay from '../shared/CurrencyDisplay'
import caloriesIcon from '../../assets/images/clor.svg'

interface ProductDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  product: {
    id: string | number
    name?: string
    price?: number
    image?: string
  }
  /** Cashier delivery: block add-to-cart until customer + address are set */
  onRequireDeliverySetup?: (reason: 'customer' | 'address') => void
  /**
   * Optional alternative to add-to-cart: used in "Edit Order" flow.
   * When provided, the modal builds the same meal/additionals payload but
   * hands it to the caller (which adds it to the local edit session) instead
   * of dispatching cart actions. Returns/rejects to control modal close.
   */
  onAddToOrder?: (item: {
    itemId: number
    quantity: number
    meals: Array<{
      mealId: number
      additionals: Array<{ id: number }>
    }>
  }, productInfo: {
    id: string | number
    name?: string
    price?: number
    image?: string
  }) => void | Promise<void>
}

interface SelectedMeal {
  mealId: number
  sizeId: number
  additionals: number[]
  drinks: number[]
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  isOpen,
  onClose,
  product,
  onRequireDeliverySetup,
  onAddToOrder
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  // Get branch ID from auth (logged-in user's branch)
  // For CS users, this might be null - they use csBranchId instead
  const branchId = useSelector((state: RootState) => {
    // أولوية: استخدام branch ID من بيانات تسجيل الدخول
    const user = state.auth.user
    if (user?.branch?.id) {
      const branchId = typeof user.branch.id === 'number' ? user.branch.id : Number(user.branch.id)
      if (!isNaN(branchId) && branchId > 0) {
        return branchId
      }
    }
    // Fallback: استخدام branch ID من localStorage
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.branch?.id) {
          const branchId = typeof employeeData.branch.id === 'number'
            ? employeeData.branch.id
            : Number(employeeData.branch.id)
          if (!isNaN(branchId) && branchId > 0) {
            return branchId
          }
        }
      }
    } catch (e) {
    }
    // Return null instead of throwing - will use csBranchId for CS users
    return null
  })
  const isRTL = useSelector((state: RootState) => state.settings.settings.language === 'ar')
  const { customer_id, order_type, table_id, address_id } = useSelector((state: RootState) => state.cart)
  const { user } = useSelector((state: RootState) => state.auth)
  const { hasCustomerData, customerId: customerStateId } = useSelector((state: RootState) => state.customer)
  const { discountCode } = useSelector((state: RootState) => state.discount)

  // Get CS Flow state
  const csCustomerId = useSelector((state: RootState) => state.csFlow.customerId)
  const csBranchId = useSelector((state: RootState) => state.csFlow.selectedBranchId)
  const csTypeId = useSelector(selectSelectedTypeId)
  const csAddressId = useSelector((state: RootState) => state.csFlow.selectedAddressId)

  // Check if current user is Customer Service (CS)
  const isCustomerService = useMemo(() => {
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        const userType = employeeData.user_type || employeeData.type || ''
        return userType.toString().toLowerCase().includes('cs') ||
          userType.toString().toLowerCase().includes('customer_service')
      }
      return false
    } catch (e) {
      return false
    }
  }, [])

  // Get cashier ID (the logged-in employee) - this should NEVER be customer_id
  // The API returns employee.user_id (7) which is the actual user ID, not employee.id (1)
  const getCashierId = (): number => {
    // First priority: use user_id from Redux state (from auth.user)
    // The employee object has user_id field which is the actual user ID
    const userAny = user as any
    if (userAny?.user_id) {
      return Number(userAny.user_id)
    }

    // Also check user.user.id (nested user object)
    if (userAny?.user?.id) {
      return Number(userAny.user.id)
    }

    // Fallback 1: try to get from localStorage user_data (saved during login)
    try {
      const userDataStr = localStorage.getItem('user_data')
      if (userDataStr) {
        const userData = JSON.parse(userDataStr)
        // Check user_id first (this is the actual user ID)
        if (userData.user_id) {
          return Number(userData.user_id)
        }
        // Also check nested user.id
        if (userData.user?.id) {
          return Number(userData.user.id)
        }
        // Last resort: check id (but this is employee.id, not user.id)
        if (userData.id && !userData.user_id) {
        }
      }
    } catch (e) {
    }

    // Fallback 2: try to get from localStorage employee_data
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        // Check user_id first
        if (employeeData.user_id) {
          return Number(employeeData.user_id)
        }
        // Check nested user.id
        if (employeeData.user?.id) {
          return Number(employeeData.user.id)
        }
      }
    } catch (e) {
    }

    // Last resort: throw error instead of using default 1
    throw new Error('Cashier ID not found. Please login again.')
  }

  // Get userId for the order - only return customer ID if customer exists, otherwise undefined
  // userId should be the customer ID, not the cashier ID
  const getUserId = (): number | undefined => {
    // Priority 1: Check state.customer (most reliable source)
    if (hasCustomerData && customerStateId) {
      return Number(customerStateId)
    }

    // Priority 2: Check state.cart.customer_id (fallback)
    if (customer_id) {
      return Number(customer_id)
    }

    // No customer exists - return undefined (don't use cashier ID)
    return undefined
  }

  const cashierId = getCashierId()
  const userId = getUserId()
  // Map order_type to typeId (1: delivery, 2: takeaway, 3: dine_in, 4: car_pickup)
  const getTypeId = (orderType: string): number => {
    switch (orderType) {
      case 'delivery': return 1
      case 'takeaway': return 2
      case 'dine_in': return 3
      case 'car_pickup': return 4
      default: return 3
    }
  }
  const typeId = getTypeId(order_type || 'dine_in')

  const [selectedMeal, setSelectedMeal] = useState<SelectedMeal | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [groupErrors, setGroupErrors] = useState<Record<string, boolean>>({})
  const groupRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({})

  // API hooks
  const [addToCartWithItems] = useAddToCartWithItemsMutation()
  const [fetchCartData] = useLazyGetCartDataQuery()

  // Get active branch ID (CS users use csBranchId, regular users use branchId)
  const activeBranchId = useMemo(() => {
    if (isCustomerService && csBranchId) {
      return csBranchId
    }
    return branchId
  }, [isCustomerService, csBranchId, branchId])

  // Fetch product details
  const { data: productData, isLoading } = useGetCashierProductDetailsQuery(
    { productId: product.id, branchId: activeBranchId },
    { skip: !isOpen || !product.id || !activeBranchId }
  )

  // Initialize selected meal when data loads
  useEffect(() => {
    if (productData?.meals && productData.meals.length > 0) {
      const firstMeal = productData.meals[0]
      setSelectedMeal({
        mealId: firstMeal.id,
        sizeId: firstMeal.size.id,
        additionals: [],
        drinks: []
      })
    }
  }, [productData])

  // Reset selections when meal changes
  const prevMealIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (selectedMeal && productData?.meals) {
      const currentMealId = selectedMeal.mealId

      // Only reset if meal actually changed (not on initial load)
      if (prevMealIdRef.current !== null && prevMealIdRef.current !== currentMealId) {
        // Clear all previous selections when meal changes
        setSelectedMeal(prev => {
          if (!prev) return prev
          return {
            ...prev,
            additionals: [],
            drinks: []
          }
        })
        // Clear all group errors
        setGroupErrors({})
      }

      // Update ref
      prevMealIdRef.current = currentMealId
    }
  }, [selectedMeal?.mealId, productData?.meals])

  // Get current meal data
  const currentMeal = useMemo(() => {
    if (!productData?.meals || !selectedMeal) return null
    return productData.meals.find(m => m.id === selectedMeal.mealId)
  }, [productData, selectedMeal])

  // Group meals by category
  const mealsByCategory = useMemo(() => {
    if (!productData?.meals) return {}

    const grouped: Record<string, typeof productData.meals> = {}
    productData.meals.forEach(meal => {
      const categoryName = meal.category.name
      if (!grouped[categoryName]) {
        grouped[categoryName] = []
      }
      grouped[categoryName].push(meal)
    })
    return grouped
  }, [productData])

  // Get sizes (all meals that have size property - these are the size options)
  const sizes = useMemo(() => {
    if (!productData?.meals) return []
    // All meals represent sizes, regardless of category
    // If product is in "المشروبات" category, its meals are still sizes (like كبير/صغير)
    return productData.meals.filter(meal => meal.size && meal.size.id)
  }, [productData])

  // Get additions (from additionalsGroup) - grouped by group name
  const additionalGroups = useMemo(() => {
    if (!currentMeal || !currentMeal.additionalsGroup) return {}

    const groups: Record<string, {
      items: any[]
      is_required: boolean
      is_multi_select: boolean
    }> = {}

    currentMeal.additionalsGroup.forEach((group: any) => {
      if (!group.additionals || !Array.isArray(group.additionals)) return

      const groupName = group.group || group.name || 'الاضافات'

      if (!groups[groupName]) {
        groups[groupName] = {
          items: [],
          is_required: group.is_required || false,
          is_multi_select: group.is_multi_select !== undefined ? group.is_multi_select : true
        }
      }

      // Add items to group (avoid duplicates)
      group.additionals.forEach((item: any) => {
        const exists = groups[groupName].items.some(existing =>
          parseInt(existing.id.toString()) === parseInt(item.id.toString())
        )
        if (!exists) {
          groups[groupName].items.push(item)
        }
      })
    })

    return groups
  }, [currentMeal])

  // Get additions (from additionalsGroup) - for backward compatibility
  const additions = useMemo(() => {
    if (!currentMeal) return []
    return currentMeal.additionalsGroup || []
  }, [currentMeal])

  // Get drinks (from additionalsGroup with group name "مشروبات" or similar)
  // Drinks are now part of additions, not separate meals
  const drinksData = useMemo(() => {
    // Check if there's an additionalsGroup named "مشروبات" or contains "مشروب"
    if (!currentMeal || !currentMeal.additionalsGroup) return { drinks: [], group: null }

    const drinksGroup = currentMeal.additionalsGroup.find((group: any) => {
      const groupName = (group.group || group.name || '').toLowerCase()
      return groupName.includes('مشروب') || groupName.includes('drink')
    })

    return {
      drinks: drinksGroup?.additionals || [],
      group: drinksGroup || null
    }
  }, [currentMeal])

  const drinks = drinksData.drinks
  const drinksGroup = drinksData.group

  // Initialize ref for drinks group if not exists
  useEffect(() => {
    if (drinksGroup) {
      const drinksGroupName = drinksGroup.group || drinksGroup.name || 'مشروبات'
      if (!groupRefs.current[drinksGroupName]) {
        groupRefs.current[drinksGroupName] = React.createRef<HTMLDivElement>()
      }
    }
  }, [drinksGroup])

  // Calculate total price
  const totalPrice = useMemo(() => {
    if (!currentMeal) return parseFloat(productData?.price || '0')

    let basePrice = parseFloat(currentMeal.price || '0')

    // Add additionals price (includes drinks from additionalsGroup)
    if (selectedMeal && currentMeal.additionalsGroup) {
      currentMeal.additionalsGroup.forEach((group: any) => {
        if (group.additionals && Array.isArray(group.additionals)) {
          group.additionals.forEach((additional: any) => {
            if (selectedMeal.additionals.includes(additional.id)) {
              basePrice += parseFloat(additional.price || '0')
            }
          })
        }
      })
    }

    return basePrice * quantity
  }, [currentMeal, selectedMeal, quantity, productData])

  const handleSizeSelect = (mealId: number, sizeId: number) => {
    if (!selectedMeal) return
    setSelectedMeal({
      ...selectedMeal,
      mealId,
      sizeId
    })
  }

  // Validate required additions
  const validateAdditions = (): { isValid: boolean; invalidGroupName: string | null } => {
    // التحقق من جميع المجموعات المطلوبة في additionalGroups
    for (const [groupName, groupData] of Object.entries(additionalGroups)) {
      const group = groupData as any

      if (group.is_required) {
        const selectedInGroup = group.items.some((item: any) =>
          selectedMeal?.additionals.includes(parseInt(item.id.toString()))
        )

        if (!selectedInGroup) {
          return {
            isValid: false,
            invalidGroupName: groupName
          }
        }
      }
    }

    // التحقق من المشروبات المطلوبة بشكل منفصل
    if (drinksGroup && drinksGroup.is_required) {
      const selectedDrink = drinks.some((drink: any) =>
        selectedMeal?.additionals.includes(parseInt(drink.id.toString()))
      )

      if (!selectedDrink) {
        const drinksGroupName = drinksGroup.group || drinksGroup.name || 'مشروبات'
        return {
          isValid: false,
          invalidGroupName: drinksGroupName
        }
      }
    }

    return { isValid: true, invalidGroupName: null }
  }

  const handleAdditionalToggle = (additionalId: number, isMultiSelect: boolean = false, groupIndex?: number, groupAdditionals?: any[]) => {
    if (!selectedMeal || !currentMeal) return

    const numericId = parseInt(additionalId.toString())
    if (isNaN(numericId)) return

    // Find which group this extra belongs to
    const extraGroup = Object.entries(additionalGroups).find(([_, group]: [string, any]) =>
      group.items.some((item: any) => parseInt(item.id.toString()) === numericId)
    )

    const groupData = extraGroup ? extraGroup[1] : null
    const groupName = extraGroup ? extraGroup[0] : null
    const isMultiSelectFromGroup = groupData?.is_multi_select !== false

    setSelectedMeal(prev => {
      if (!prev) return prev

      const normalizedPrev = prev.additionals.map(item => parseInt(item.toString())).filter(item => !isNaN(item))
      const isSelected = normalizedPrev.includes(numericId)

      if (isSelected) {
        // Remove the item
        const newAdditionals = normalizedPrev.filter(item => item !== numericId)

        // Clear error state for this group
        if (groupName) {
          setGroupErrors(prevErrors => {
            if (prevErrors[groupName]) {
              return { ...prevErrors, [groupName]: false }
            }
            return prevErrors
          })
        }

        return {
          ...prev,
          additionals: newAdditionals
        }
      } else {
        if (isMultiSelectFromGroup) {
          // Multi-select: Add the item
          const newExtras = [...normalizedPrev, numericId]
          const result = Array.from(new Set(newExtras))

          // Clear error state
          if (groupName) {
            setGroupErrors(prevErrors => {
              if (prevErrors[groupName]) {
                return { ...prevErrors, [groupName]: false }
              }
              return prevErrors
            })
          }

          return {
            ...prev,
            additionals: result
          }
        } else {
          // Single-select: Remove other items from the same group
          const otherGroupIds = groupData?.items.map((item: any) => parseInt(item.id.toString())) || []
          const filtered = normalizedPrev.filter(item => !otherGroupIds.includes(item))
          const result = [...filtered, numericId]

          // Clear error state
          if (groupName) {
            setGroupErrors(prevErrors => {
              if (prevErrors[groupName]) {
                return { ...prevErrors, [groupName]: false }
              }
              return prevErrors
            })
          }

          return {
            ...prev,
            additionals: result
          }
        }
      }
    })
  }

  const handleDrinkToggle = (drinkId: number) => {
    if (!selectedMeal) return
    const isSelected = selectedMeal.drinks.includes(drinkId)
    setSelectedMeal({
      ...selectedMeal,
      drinks: isSelected
        ? selectedMeal.drinks.filter(id => id !== drinkId)
        : [...selectedMeal.drinks, drinkId]
    })
  }

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.max(1, prev + delta))
  }

  const handleAddToCart = async () => {
    if (!productData || !selectedMeal || !currentMeal || isAddingToCart) return

    // Prepare the API payload according to the provided format
    // itemId should be the meal id (18 or 19), not the product id (9)
    // additionals must be array of objects with id property: [{"id": 6}]
    // Convert additionals to array of objects with id property
    const additionalsArray: Array<{ id: number }> = []
    if (Array.isArray(selectedMeal.additionals)) {
      for (const adj of selectedMeal.additionals) {
        if (typeof adj === 'number') {
          // Convert number to object with id property
          additionalsArray.push({ id: adj })
        } else if (adj && typeof adj === 'object' && adj !== null && 'id' in adj) {
          // Extract id from object {id: 6} and ensure it's a valid number
          const adjObj = adj as { id: number | string }
          const id = Number(adjObj.id)
          if (!isNaN(id) && id > 0) {
            additionalsArray.push({ id: id })
          }
        } else if (typeof adj === 'string') {
          const num = Number(adj)
          if (!isNaN(num) && num > 0) {
            additionalsArray.push({ id: num })
          }
        }
      }
    }

    // Final validation: ensure additionalsArray contains only valid objects with id
    const validatedAdditionals = additionalsArray
      .filter((a) => a && typeof a === 'object' && 'id' in a && typeof a.id === 'number' && !isNaN(a.id) && a.id > 0)
      .map(a => ({ id: Number(a.id) }))

    // Edit Order flow: hand the payload to the caller instead of cart dispatch
    if (onAddToOrder) {
      // Validate required additions
      const editValidation = validateAdditions()
      if (!editValidation.isValid) {
        setGroupErrors(prev => ({
          ...prev,
          [editValidation.invalidGroupName!]: true
        }))
        if (!groupRefs.current[editValidation.invalidGroupName!]) {
          groupRefs.current[editValidation.invalidGroupName!] = React.createRef<HTMLDivElement>()
        }
        groupRefs.current[editValidation.invalidGroupName!]?.current?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        })
        return
      }

      try {
        setIsAddingToCart(true)
        await onAddToOrder({
          itemId: Number(selectedMeal.mealId),
          quantity: quantity,
          meals: [
            {
              mealId: Number(selectedMeal.mealId),
              additionals: validatedAdditionals
            }
          ]
        }, {
          id: product.id,
          name: productData?.name || product.name,
          price: parseFloat(productData?.price || currentMeal?.price || '0') || 0,
          image: productData?.image || product.image,
        })
        onClose()
      } catch (error: any) {
        // Keep modal open so the user can retry
      } finally {
        setIsAddingToCart(false)
      }
      return
    }

    // Cashier delivery: require customer + address before add-to-cart
    if (!isCustomerService && order_type === 'delivery') {
      const hasCustomer = Boolean(hasCustomerData && customerStateId) || Boolean(customer_id)
      const storedAddressId = (() => {
        try {
          const stored = localStorage.getItem('selected_address_id')
          return stored ? Number(stored) : 0
        } catch {
          return 0
        }
      })()
      const hasAddress =
        (address_id != null && Number(address_id) > 0) ||
        storedAddressId > 0

      if (!hasCustomer) {
        onClose()
        onRequireDeliverySetup?.('customer')
        return
      }

      if (!hasAddress) {
        onClose()
        onRequireDeliverySetup?.('address')
        return
      }
    }

    // Validate required additions
    const validation = validateAdditions()
    if (!validation.isValid) {
      // Set error state for the invalid group
      setGroupErrors(prev => ({
        ...prev,
        [validation.invalidGroupName!]: true
      }))

      // Scroll to the invalid group
      if (!groupRefs.current[validation.invalidGroupName!]) {
        groupRefs.current[validation.invalidGroupName!] = React.createRef<HTMLDivElement>()
      }
      groupRefs.current[validation.invalidGroupName!]?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      })

      return
    }

    try {
      setIsAddingToCart(true)
      dispatch(setLoading(true))
      dispatch(setError(null))

      // Check if CS user and use CS flow data
      if (isCustomerService && csCustomerId > 0) {
        // CS Flow: Use customer ID, CS branch, CS type, and CS address
        const payload: any = {
          items: [
            {
              itemId: Number(selectedMeal.mealId), // Use meal id (18 or 19) not product id (9)
              quantity: quantity,
              meals: [
                {
                  mealId: Number(selectedMeal.mealId),
                  additionals: validatedAdditionals // Must be array of objects [{"id": 6}]
                }
              ]
            }
          ],
          platform: 'customer_service',
          typeId: csTypeId || 1,
          branchId: csBranchId || branchId,
          userId: csCustomerId // Use customer ID, not CS ID
        }

        // Add addressId if delivery (typeId = 1)
        if (csTypeId === 1 && csAddressId) {
          payload.addressId = csAddressId
        }

        // Call CS-specific endpoint
        const response = await apiService.axios.post(
          buildEndpoint('/cashier/add-to-cart', csCustomerId),
          payload
        )

        // Refresh CS cart items immediately BEFORE closing modal
        // Use forceRefresh: true to bypass cache and get latest data
        // GET /customerService/carts/user/{customerId}
        try {
          await dispatch(refreshCsCartItems({ customerId: csCustomerId, forceRefresh: true }) as any)
        } catch (cartError) {
        }

        // Close modal after cart is refreshed
        onClose()

        return
      }

      // Regular flow (non-CS)
      const payload: any = {
        items: [
          {
            itemId: Number(selectedMeal.mealId), // Use meal id (18 or 19) not product id (9)
            quantity: quantity,
            meals: [
              {
                mealId: Number(selectedMeal.mealId),
                additionals: validatedAdditionals // Must be array of objects [{"id": 6}]
              }
            ]
          }
        ],
        platform: 'cashier',
        typeId: typeId,
        branchId: branchId
      }

      // Always add creatorId (cashier ID) - this is the logged-in employee who opened the account
      payload.creatorId = cashierId

      // Add userId only if customer exists - should be customer ID, not cashier ID
      if (userId !== undefined) {
        payload.userId = userId
      }

      // Add tableId if exists
      if (table_id) {
        payload.tableId = Number(table_id)
      }

      // Add addressId for delivery orders
      if (typeId === 1) {
        const deliveryAddressId = address_id
          ? Number(address_id)
          : (localStorage.getItem('selected_address_id')
            ? Number(localStorage.getItem('selected_address_id'))
            : undefined)
        if (deliveryAddressId && deliveryAddressId > 0) {
          payload.addressId = deliveryAddressId
        }
      }

      // Final validation before sending
      // Ensure meals[0].additionals is array of objects with id property
      if (payload.items && payload.items[0] && payload.items[0].meals && payload.items[0].meals[0]) {
        const mealAdditionals = payload.items[0].meals[0].additionals
        if (Array.isArray(mealAdditionals)) {
          payload.items[0].meals[0].additionals = mealAdditionals
            .filter((a: any) => a && typeof a === 'object' && 'id' in a && typeof a.id === 'number' && !isNaN(a.id) && a.id > 0)
            .map((a: any) => ({ id: Number(a.id) }))
        }
      }

      // Call the add-to-cart API
      const addResult = await addToCartWithItems(payload).unwrap()

      // Close modal immediately after successful add
      onClose()

      // Reload cart items from API after adding (non-blocking)
      try {
        // Get table_id from cart state if available
        const tableId = table_id ? Number(table_id) : undefined

        // Get coupon code from discount state if available
        const couponCode = discountCode && discountCode.trim() ? discountCode.trim() : undefined

        // Force refresh cart items from API (updates cartItemsSlice)
        // Include coupon code to ensure discount is applied to all items
        await dispatch(loadCartItems({
          forceRefresh: true,
          couponCode: couponCode,
          tableId: tableId
        }) as any)

        // Also update cartSlice for HomePage display — only when a valid
        // customer/cart userId exists. Never call /cashier/carts/user/undefined.
        if (userId != null && Number(userId) > 0) {
          const cartDataResult = await fetchCartData({
            userId: Number(userId),
            couponCode: couponCode,
            tableId: tableId
          }).unwrap()

          if (cartDataResult) {
            dispatch(updateCartFromAPI(cartDataResult))
          }
        }

      } catch (cartError) {
        // Don't show error to user
      }
    } catch (error: any) {
      // RTK Query errors expose the body on `data`, axios errors on `response.data`
      const errorBody = error?.data ?? error?.response?.data
      const serverMessage: string | undefined =
        typeof errorBody?.descriptiveMessage === 'string' ? errorBody.descriptiveMessage : undefined
      const displayMessage =
        serverMessage === 'No available items found'
          ? t('product.item_unavailable')
          : serverMessage || t('product.add_to_cart_failed')

      dispatch(setError(displayMessage))
      toast.error(displayMessage, {
        duration: 4000,
        position: 'top-center',
        style: {
          fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
          direction: isRTL ? 'rtl' : 'ltr',
        },
      })
      // Don't close modal on error
    } finally {
      setIsAddingToCart(false)
      dispatch(setLoading(false))
    }
  }

  // Calculate calories (if available in productData)
  const calories = useMemo(() => {
    if (!currentMeal) return null
    // Try to get calories from meal data
    return (currentMeal as any).calories || null
  }, [currentMeal])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center py-20"
      dir={isRTL ? 'rtl' : 'ltr'}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Modal Container - Centered */}
      <div className="relative w-[570px] h-[640px] rounded-2xl  bg-white flex flex-col overflow-hidden">
        {/* Close Button */}
        <div className='pt-3 pr-3 pl-3 flex justify-end items-end bg-transparent '>
          <button
            onClick={onClose}
            className="w-10 h-10  bg-white rounded-xl border border-gray-200 flex items-center justify-center"
          >
            <X className="w-6 h-6 text-icon-secondary" />
          </button>

        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto flex flex-col [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {/* Header Section with Image, Close Button, and Product Info */}
          <div className="px-4 pt-3 bg-white  items-start gap-1">



            <div className='w-full h-32 flex justify-center items-center mb-4'>
              {/* Product Image */}
              <img
                className="w-[142px] h-[142px]  object-contain"
                src={productData?.image || product.image || 'https://placehold.co/112x112'}
                alt={productData?.name || product.name}
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/112x112'
                }}
              />
            </div>

            {/* Product Info */}
            <div className=" justify-center items-start gap-3">
              <h2 className="w-full  text-text-primary text-2xl font-medium leading-8 line-clamp-1">
                {productData?.name || product.name || 'منتج'}
              </h2>
              <p className="w-80 pt-3 pb-2 text-text-secondary text-base font-normal leading-6">
                {productData?.description || 'لا يوجد وصف متاح'}
              </p>
            </div>
            <div className='flex justify-start items-start gap-2 '>
              <span className="text-right text-black text-base font-semibold ">
                {totalPrice.toFixed(2)}
              </span>
              <CurrencyDisplay className="w-5 h-5" />

            </div>

          </div>

          {/* Calories Display */}
          {calories && (
            <div className="px-4 pt-3 pb-1 bg-white flex flex-row gap-3">
              <div className="h-6 flex justify-start items-start gap-1">
                <img src={caloriesIcon} alt="calories" className="w-5 h-5" />
                <span className="flex-1 text-right text-black text-base font-medium leading-6">
                  {t('product.calories_label', { calories })}
                </span>

              </div>
            </div>
          )}

          {/* Sizes Section */}
          {sizes.length > 0 && (
            <div className="px-4 pt-3 pb-4 bg-white flex flex-col gap-3">
              <h3 className=" text-text-primary text-base font-semibold leading-6">
                {t('product.size')}
              </h3>
              <div className="flex justify-start items-center gap-3 flex-wrap">
                {sizes.map((meal, index) => {
                  const isSelected = selectedMeal?.mealId === meal.id
                  // Use the actual price from API for all sizes, not the price difference
                  const currentPrice = parseFloat(meal.price || '0')
                  const optionPrice = currentPrice
                  return (
                    <div
                      key={meal.id}
                      className="relative"
                      style={{ marginBottom: optionPrice !== 0 ? '15px' : '0' }}
                    >
                      {/* الكارت الرئيسي */}
                      <div
                        className={`w-28 h-28 p-1.5 rounded-xl cursor-pointer transition-all duration-100 flex items-center justify-center ${isSelected
                          ? 'bg-white border-2'
                          : 'bg-white border border-gray-300'
                          }`}
                        style={isSelected ? { borderColor: 'var(--brand-primary)' } : {}}
                        onClick={() => handleSizeSelect(meal.id, meal.size.id)}
                      >
                        <div className="flex-1 flex flex-col justify-center items-center gap-1 relative w-full h-full">
                          {/* دائرة الاختيار في الجانب الأيمن/الأيسر */}
                          <div
                            className="absolute rounded-full"
                            style={{
                              top: '3px',
                              [isRTL ? 'right' : 'left']: '3px',
                              width: '20px',
                              height: '20px'
                            }}
                          >
                            <div
                              className="w-full h-full rounded-full border-2 flex items-center justify-center"
                              style={isSelected ? {
                                backgroundColor: 'var(--brand-primary)',
                                borderColor: 'var(--brand-primary)'
                              } : {
                                backgroundColor: 'white',
                                borderColor: '#9ca3af'
                              }}
                            >
                              {isSelected && (
                                <div
                                  className="rounded-full border-15 border-white "
                                  style={{
                                    width: '15px',
                                    height: '15px',
                                    backgroundColor: 'flutter-button',
                                    borderRadius: '50%',
                                    border: '2px solid white'
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          {/* اسم المنتج */}
                          <div className={`text-center text-xs font-medium leading-4 mt-2 ${isSelected ? 'text-flutter-button font-bold' : 'text-text-primary font-medium'
                            }`}>
                            {meal.size.name}
                          </div>
                        </div>
                      </div>

                      {/* دائرة السعر خارج الكارت في الأسفل - تظهر فقط إذا كان السعر غير 0 */}
                      {optionPrice !== 0 && (
                        <div
                          className={`absolute h-9 px-1.5 rounded-full flex items-center justify-center gap-0.5 ${isSelected
                            ? 'bg-white border-2'
                            : 'bg-white border border-gray-400'
                            }`}
                          style={{
                            bottom: '-15px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            minWidth: '60px',
                            ...(isSelected ? { borderColor: 'var(--brand-primary)' } : {})
                          }}
                        >
                          <span className={`text-xs font-normal ${isSelected ? 'text-black' : 'text-gray-900'
                            }`}>
                            {optionPrice.toFixed(2)}
                          </span>
                          <CurrencyDisplay className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Additions Section - Grouped */}
          {additions.length > 0 && (
            <div className="px-4 pt-3 pb-4 bg-white flex flex-col gap-6">
              {additions.map((group: any, groupIndex: number) => {
                if (!group.additionals || !Array.isArray(group.additionals) || group.additionals.length === 0) return null

                const groupName = group.group || group.name || 'الاضافات'
                // Skip drinks group if it's displayed in separate drinks section
                const isDrinksGroup = (groupName.toLowerCase().includes('مشروب') || groupName.toLowerCase().includes('drink'))
                if (isDrinksGroup && drinks.length > 0 && drinksGroup) return null

                const isRequired = group.is_required || false
                const isMultiSelect = group.is_multi_select !== undefined ? group.is_multi_select : true

                // Initialize ref for this group if not exists
                if (!groupRefs.current[groupName]) {
                  groupRefs.current[groupName] = React.createRef<HTMLDivElement>()
                }

                const hasError = groupErrors[groupName] || false

                return (
                  <div
                    key={groupIndex}
                    ref={groupRefs.current[groupName]}
                    className="flex flex-col gap-3"
                  >
                    {/* عنوان المجموعة */}
                    <div className="flex items-center gap-2">
                      <h3 className="text-right text-text-primary text-base font-semibold leading-6">
                        {groupName}
                      </h3>
                      {isRequired && (
                        <span className="text-xs text-red-500 font-medium">*</span>
                      )}
                    </div>

                    {/* رسالة توضيحية */}
                    <p className=" text-text-secondary text-xs font-normal leading-4">
                      {isMultiSelect ? t('product.can_select_multiple') : t('product.select_one_item')}
                    </p>

                    {/* رسالة خطأ */}
                    {hasError && (
                      <p className=" text-red-500 text-xs font-medium leading-4">
                        {t('product.must_select_at_least_one')}
                      </p>
                    )}

                    {/* عناصر المجموعة */}
                    <div className="flex justify-start items-center gap-3 flex-wrap">
                      {group.additionals.map((additional: any) => {
                        const isSelected = selectedMeal?.additionals.includes(additional.id) || false
                        const optionPrice = parseFloat(additional.price || '0')
                        return (
                          <div
                            key={additional.id}
                            className="relative"
                            style={{ marginBottom: optionPrice !== 0 ? '15px' : '0' }}
                          >
                            {/* الكارت الرئيسي */}
                            <div
                              className={`w-[135px] h-[135px] p-2  rounded-xl cursor-pointer transition-all duration-100 flex items-center justify-center ${isSelected
                                ? 'bg-white border-2'
                                : 'bg-white border border-gray-300'
                                }`}
                              style={isSelected ? { borderColor: 'var(--brand-primary)' } : {}}
                              onClick={() => handleAdditionalToggle(additional.id, isMultiSelect, groupIndex, group.additionals)}
                            >
                              <div className="flex-1 flex flex-col justify-start items-center gap-2 relative w-full h-full ">
                                {/* دائرة الاختيار في الجانب الأيمن/الأيسر */}
                                <div
                                  className="absolute rounded-full z-10 text-flutter-button"
                                  style={{
                                    top: '3px',
                                    [isRTL ? 'right' : 'left']: '3px',
                                    width: '20px',
                                    height: '20px'
                                  }}
                                >
                                  <div
                                    className="w-full h-full rounded-full border-2 flex items-center justify-center"
                                    style={isSelected ? {
                                      backgroundColor: 'var(--brand-primary)',
                                      borderColor: 'var(--brand-primary)'
                                    } : {
                                      backgroundColor: 'white',
                                      borderColor: '#9ca3af'
                                    }}
                                  >
                                    {isSelected && (
                                      <div
                                        className="rounded-full border-15 border-white "
                                        style={{
                                          width: '15px',
                                          height: '15px',
                                          backgroundColor: 'flutter-button',
                                          borderRadius: '50%',
                                          border: '2px solid white'
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>

                                {/* صورة الإضافة */}
                                <div className=" flex items-center justify-center overflow-hidden rounded-lg">
                                  {additional.image && additional.image !== '/p.png' ? (
                                    <img
                                      src={additional.image}
                                      alt={additional.name || 'إضافة'}
                                      className="w-20 h-20 "
                                      onError={(e) => {
                                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-size="30"%3E🍽️%3C/text%3E%3C/svg%3E'
                                      }}
                                    />
                                  ) : (
                                    <div className="w-20 h-20 flex items-center justify-center bg-gray-100">
                                      <div className="text-3xl">🍽️</div>
                                    </div>
                                  )}
                                </div>

                                {/* اسم المنتج */}
                                <div className={`text-center text-sm font-medium leading-25    ${isSelected ? 'text-flutter-button font-bold' : 'text-text-primary font-medium'
                                  }`}>
                                  {additional.name || 'إضافة'}
                                </div>

                              </div>
                            </div>

                            {/* دائرة السعر خارج الكارت في الأسفل - تظهر فقط إذا كان السعر غير 0 */}
                            {optionPrice !== 0 && (
                              <div
                                className={`absolute h-7 px-1.5  rounded-full flex items-center justify-center gap-0.5 ${isSelected
                                  ? 'bg-white border-2'
                                  : 'bg-white border border-gray-400'
                                  }`}
                                style={{
                                  bottom: '-15px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  minWidth: '60px',
                                  ...(isSelected ? { borderColor: 'var(--brand-primary)' } : {})
                                }}
                              >
                                <span className={`text-xs font-normal ${isSelected ? 'text-black' : 'text-gray-900'
                                  }`}>
                                  {optionPrice > 0 ? `${optionPrice.toFixed(2)}` : optionPrice.toFixed(2)}
                                </span>
                                <CurrencyDisplay className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Drinks Section */}
          {drinks.length > 0 && drinksGroup && (
            <div
              ref={groupRefs.current[drinksGroup.group || drinksGroup.name || 'مشروبات']}
              className="px-4 pt-3 pb-4 bg-white flex flex-col gap-3"
            >
              <div className="flex items-center gap-2">
                <h3 className=" text-text-primary text-base font-semibold leading-6">
                  {drinksGroup.group || drinksGroup.name || 'المشروبات'}
                </h3>
                {drinksGroup.is_required && (
                  <span className="text-xs text-red-500 font-medium">*</span>
                )}
              </div>

              {/* رسالة توضيحية */}
              <p className=" text-text-secondary text-xs font-normal leading-4">
                {drinksGroup.is_multi_select !== false
                  ? t('product.can_select_multiple')
                  : t('product.select_one_item')}
              </p>

              {/* رسالة خطأ */}
              {groupErrors[drinksGroup.group || drinksGroup.name || 'مشروبات'] && (
                <p className=" text-red-500 text-xs font-medium leading-4">
                  {t('product.must_select_at_least_one')}
                </p>
              )}

              <div className="flex justify-start items-center gap-3 flex-wrap">
                {drinks.map((drink) => {
                  const isSelected = selectedMeal?.additionals.includes(drink.id) || false
                  // استخدام السعر مباشرة من API
                  const optionPrice = parseFloat(drink.price || '0')
                  const isMultiSelect = drinksGroup.is_multi_select !== false
                  return (
                    <div
                      key={drink.id}
                      className="relative"
                      style={{ marginBottom: optionPrice !== 0 ? '15px' : '0' }}
                    >
                      {/* الكارت الرئيسي */}
                      <div
                        className={`w-[135px] h-[135px] p-2  rounded-xl cursor-pointer transition-all duration-100 flex items-center justify-center ${isSelected
                          ? 'bg-white border-2'
                          : 'bg-white border border-gray-300'
                          }`}
                        style={isSelected ? { borderColor: 'var(--brand-primary)' } : {}}
                        onClick={() => handleAdditionalToggle(drink.id, isMultiSelect)}
                      >
                        <div className="flex-1 flex flex-col justify-start items-center gap-2 relative w-full h-full ">
                          {/* دائرة الاختيار في الجانب الأيمن/الأيسر */}
                          <div
                            className="absolute rounded-full z-10 text-flutter-button"
                            style={{
                              top: '3px',
                              [isRTL ? 'right' : 'left']: '3px',
                              width: '20px',
                              height: '20px'
                            }}
                          >
                            <div
                              className="w-full h-full rounded-full border-2 flex items-center justify-center"
                              style={isSelected ? {
                                backgroundColor: 'var(--brand-primary)',
                                borderColor: 'var(--brand-primary)'
                              } : {
                                backgroundColor: 'white',
                                borderColor: '#9ca3af'
                              }}
                            >
                              {isSelected && (
                                <div
                                  className="rounded-full border-15 border-white "
                                  style={{
                                    width: '15px',
                                    height: '15px',
                                    backgroundColor: 'flutter-button',
                                    borderRadius: '50%',
                                    border: '2px solid white'
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          {/* صورة المشروب من API */}
                          <div className=" flex items-center justify-center overflow-hidden rounded-lg">
                            {drink.image && drink.image !== '/p.png' ? (
                              <img
                                src={drink.image}
                                alt={drink.name || 'مشروب'}
                                className="w-20 h-20 object-contain"
                                onError={(e) => {
                                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-size="30"%3E🥤%3C/text%3E%3C/svg%3E'
                                }}
                              />
                            ) : (
                              <div className="w-20 h-20 flex items-center justify-center bg-gray-100">
                                <div className="text-3xl">🥤</div>
                              </div>
                            )}
                          </div>

                          {/* اسم المنتج */}
                          <div className={`text-center text-sm font-medium leading-25    ${isSelected ? 'text-flutter-button font-bold' : 'text-text-primary font-medium'
                            }`}>
                            {drink.name || drink.title || 'مشروب'}
                          </div>

                        </div>
                      </div>

                      {/* دائرة السعر خارج الكارت في الأسفل - تظهر فقط إذا كان السعر غير 0 */}
                      {optionPrice !== 0 && (
                        <div
                          className={`absolute h-7 px-1.5  rounded-full flex items-center justify-center gap-0.5 ${isSelected
                            ? 'bg-white border-2'
                            : 'bg-white border border-gray-400'
                            }`}
                          style={{
                            bottom: '-15px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            minWidth: '60px',
                            ...(isSelected ? { borderColor: 'var(--brand-primary)' } : {})
                          }}
                        >
                          <span className={`text-xs font-normal ${isSelected ? 'text-black' : 'text-gray-900'
                            }`}>
                            {optionPrice.toFixed(2)}
                          </span>
                          <CurrencyDisplay className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="h-20 flex flex-col justify-end items-end">


          {/* Add to Cart and Quantity */}
          <div className="self-stretch p-4 bg-white border-t border-border-soft flex justify-start items-start gap-4">
            {/* Quantity Selector */}
            <div className="bg-white rounded-lg border border-border-soft flex justify-start items-center overflow-hidden">
              <button
                onClick={() => handleQuantityChange(1)}
                className="w-12 h-12 bg-white flex justify-center items-center gap-1"
              >
                <Plus className="w-5 h-5 text-icon-primary" />
              </button>

              <div className="w-14 h-12 min-w-12 p-2 border-l border-r border-zinc-200 flex justify-center items-center">
                <span className="text-center text-text-primary text-sm font-semibold leading-5">
                  {quantity}
                </span>
              </div>
              <button
                onClick={() => handleQuantityChange(-1)}
                className="w-12 h-12 bg-white flex justify-center items-center gap-1"
              >
                <Minus className="w-5 h-5 text-icon-primary" />
              </button>
            </div>


            {/* Add to Cart Button */}

            <button
              onClick={handleAddToCart}
              className="flex-1 h-12 px-4 bg-flutter-button rounded-lg flex justify-center items-center gap-2"
              disabled={isLoading || !selectedMeal || isAddingToCart}
            >
              <div className="flex-1 h-6 flex justify-between items-center gap-1">
                <span className="text-text-primary-on-accent text-base font-semibold leading-6">
                  {t('product.add_to_cart_button')}
                </span>
                <div className=" flex flex-row gap-2 items-center justify-center" >
                  <span className=" text-text-primary-on-accent text-base font-semibold ">
                    {totalPrice.toFixed(2)}
                  </span>
                  <CurrencyDisplay
                    className="w-5 h-5"
                    iconStyle={{ filter: 'brightness(0) invert(1)' }}
                  />
                </div>
              </div>
            </button>


          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetailsModal
